import React, { useState, useEffect } from 'react';
import chatApi from '../../services/chatApi';
import './CreateGroupModal.css';

const MAX_PHOTO_SIZE_BYTES = 500 * 1024 * 1024;

const CreateGroupModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    avatar: null,
    memberIds: []
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const userList = await chatApi.getUsers();
      setUsers(userList);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        setError('Размер файла не должен превышать 500 МБ');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setAvatarPreview(base64);
        setFormData({ ...formData, avatar: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleMember = (userId) => {
    setFormData(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(userId)
        ? prev.memberIds.filter(id => id !== userId)
        : [...prev.memberIds, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        setError('Название группы обязательно');
        setLoading(false);
        return;
      }

      if (formData.memberIds.length === 0) {
        setError('Выберите хотя бы одного участника');
        setLoading(false);
        return;
      }

      const group = await chatApi.createGroup({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        avatar: formData.avatar || null,
        memberIds: formData.memberIds
      });

      if (onSuccess) {
        onSuccess(group);
      }
      
      // Сброс формы
      setFormData({ name: '', description: '', avatar: null, memberIds: [] });
      setAvatarPreview(null);
      setSearchQuery('');
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при создании группы');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', avatar: null, memberIds: [] });
    setAvatarPreview(null);
    setSearchQuery('');
    setError(null);
    onClose();
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const displayName = (user.displayName || user.fullName || user.username || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    return displayName.includes(query) || username.includes(query);
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Создать группу</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Аватар группы</label>
            <div className="avatar-upload">
              <div className="avatar-preview">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" />
                ) : (
                  <div className="avatar-placeholder">👥</div>
                )}
              </div>
              <label className="avatar-upload-button">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                {avatarPreview ? 'Изменить' : 'Загрузить аватар'}
              </label>
              {avatarPreview && (
                <button
                  type="button"
                  className="remove-avatar-button"
                  onClick={() => {
                    setAvatarPreview(null);
                    setFormData({ ...formData, avatar: null });
                  }}
                >
                  Удалить
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Название группы *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Введите название группы"
              required
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Описание</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Введите описание группы (необязательно)"
              rows={4}
              maxLength={500}
            />
          </div>

          <div className="form-group">
            <label>Участники * ({formData.memberIds.length} выбрано)</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск пользователей..."
              className="member-search"
            />
            <div className="members-list">
              {loadingUsers ? (
                <div className="loading-members">Загрузка пользователей...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="empty-members">Пользователи не найдены</div>
              ) : (
                filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className={`member-item ${formData.memberIds.includes(user.id) ? 'selected' : ''}`}
                    onClick={() => toggleMember(user.id)}
                  >
                    <div className="member-avatar">
                      {user.avatar ? (
                        <img src={user.avatar} alt="Avatar" />
                      ) : (
                        <span>{((user.displayName || user.fullName || user.username || '?')[0] || '?').toUpperCase()}</span>
                      )}
                    </div>
                    <div className="member-info">
                      <div className="member-name">
                        {user.displayName || user.fullName || user.username}
                      </div>
                      {user.username && (
                        <div className="member-username">@{user.username}</div>
                      )}
                    </div>
                    {formData.memberIds.includes(user.id) && (
                      <div className="member-check">✓</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="btn-cancel">
              Отмена
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Создание...' : 'Создать группу'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;
